import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Truck, ArrowClockwise, ArrowsClockwise, XCircle, CheckCircle, Warning, Receipt as ReceiptIcon,
  LinkSimple, Lightning,
} from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { EmptyState } from "@/promotions/components/EmptyState";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { TableSkeleton } from "@/promotions/components/Skeleton";
import { useOrg } from "@/context/OrgContext";
import { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Modal } from "../components/Modal";
import { deliveryApi } from "../useCreative";
import { fmtDateTime } from "../creativeUtils";

const STAT_CARDS = [
  { key: "queued", label: "Queued", tone: "text-muted-foreground" },
  { key: "scheduled", label: "Scheduled", tone: "text-brand" },
  { key: "running", label: "Running", tone: "text-warning" },
  { key: "retrying", label: "Retrying", tone: "text-warning" },
  { key: "succeeded", label: "Succeeded", tone: "text-success" },
  { key: "failed", label: "Failed", tone: "text-destructive" },
  { key: "dead_letter", label: "Dead-Letter", tone: "text-destructive" },
  { key: "cancelled", label: "Cancelled", tone: "text-muted-foreground" },
];
const LIVE = ["queued", "scheduled", "running", "retrying"];

export default function Delivery() {
  const { currentOrg } = useOrg();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [j, s] = await Promise.all([deliveryApi.listJobs(), deliveryApi.stats()]);
      setJobs(j); setStats(s);
      if (active) { const fresh = j.find((x) => x.id === active.id); if (fresh) setActive(fresh); }
    } catch { /* ignore */ }
    finally { if (!silent) setLoading(false); }
  }, [active]);

  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  // Live status: poll while any job is in a non-terminal state.
  const hasLive = useMemo(() => jobs.some((j) => LIVE.includes(j.status)), [jobs]);
  useEffect(() => {
    if (!hasLive) return;
    timer.current = setInterval(() => load(true), 3000);
    return () => clearInterval(timer.current);
  }, [hasLive, load]);

  const filtered = useMemo(() => jobs.filter((j) => !status || j.status === status), [jobs, status]);

  const act = async (fn, label) => {
    setBusy(true);
    try { await fn(); toast.success(label); await load(true); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="creative-delivery">
      <PromoPageHeader
        overline="Creative Studio · Delivery Engine"
        title="Delivery Monitor"
        description="Platform deliveries dispatched through the Connector SDK. Every platform is a connector — the engine never calls a platform API directly."
        actions={
          <button onClick={() => load()} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="delivery-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        }
      />

      {/* Live status */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="delivery-stats">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="rounded-lg border border-border bg-card p-4" data-testid={`delivery-stat-${c.key}`}>
            <div className={cn("text-2xl font-semibold tabular-nums", c.tone)}>{stats[c.key] ?? 0}</div>
            <div className="mt-1 promo-overline">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <PromoSelect
          value={status} onChange={setStatus} placeholder="All statuses" className="w-48"
          options={[{ value: "", label: "All statuses" }, ...STAT_CARDS.map((c) => ({ value: c.key, label: c.label }))]}
          testId="delivery-filter-status"
        />
        {hasLive && <span className="flex items-center gap-1.5 text-xs text-warning" data-testid="delivery-live"><Lightning size={13} weight="fill" className="animate-pulse" /> Live — auto-refreshing</span>}
        <span className="text-sm text-muted-foreground" data-testid="delivery-count">{filtered.length} job{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <TableSkeleton rows={6} cols={6} />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={Truck} title="No deliveries yet" description="Publish an approved asset — it will be delivered here through a connector." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="delivery-table">
              <thead className="promo-glass border-b border-border">
                <tr>{["Job", "Connector", "Destination", "Status", "Attempts", "Receipt", "Created"].map((h) => (<th key={h} className="px-4 py-3 text-left promo-overline whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <tr key={j.id} onClick={() => setActive(j)} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-accent/30" data-testid={`delivery-row-${j.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{j.id.slice(0, 8)}</td>
                    <td className="px-4 py-3">{j.connector_name || j.connector_id}</td>
                    <td className="px-4 py-3 text-muted-foreground">{j.destination?.destination_name || j.destination?.destination_id || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                    <td className="px-4 py-3 tabular-nums">{j.attempts}/{j.max_attempts}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{j.receipt?.external_id?.slice(0, 16) || "—"}</td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtDateTime(j.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delivery details */}
      <Modal open={!!active} onClose={() => setActive(null)} title={active ? `Delivery · ${active.connector_name || active.connector_id}` : ""} size="lg" testId="delivery-detail-modal">
        {active && (
          <div className="space-y-5 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={active.status} />
              <span className="text-muted-foreground">Attempt {active.attempts}/{active.max_attempts}</span>
              <span className="text-muted-foreground">→ {active.destination?.destination_name || active.destination?.destination_id}</span>
              <div className="ml-auto flex gap-2">
                {["failed", "cancelled", "dead_letter"].includes(active.status) && (
                  <button onClick={() => act(() => deliveryApi.retry(active.id), "Retry requeued")} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="delivery-retry">
                    <ArrowsClockwise size={14} /> Retry
                  </button>
                )}
                {!["succeeded", "cancelled"].includes(active.status) && (
                  <button onClick={() => act(() => deliveryApi.cancel(active.id), "Delivery cancelled")} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:border-destructive/40 hover:text-destructive disabled:opacity-50" data-testid="delivery-cancel">
                    <XCircle size={14} /> Cancel
                  </button>
                )}
              </div>
            </div>

            {/* Receipt viewer */}
            {active.receipt && (
              <div className="rounded-md border border-success/30 bg-success/5 p-4" data-testid="delivery-receipt">
                <div className="mb-2 flex items-center gap-1.5 font-medium text-success"><ReceiptIcon size={15} weight="fill" /> Receipt {active.receipt.simulated && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">simulated</span>}</div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  <KV label="External ID"><span className="font-mono text-xs">{active.receipt.external_id}</span></KV>
                  <KV label="Published">{fmtDateTime(active.receipt.published_at)}</KV>
                  {active.receipt.url && (
                    <div className="sm:col-span-2">
                      <a href={active.receipt.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline" data-testid="delivery-receipt-url">
                        <LinkSimple size={13} /> {active.receipt.url}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Last error */}
            {active.last_error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4" data-testid="delivery-error">
                <div className="mb-1 flex items-center gap-1.5 font-medium text-destructive"><Warning size={15} weight="fill" /> {active.last_error.type || "Error"}</div>
                <p className="text-xs text-muted-foreground">{active.last_error.message}</p>
              </div>
            )}

            {/* Attempt history + connector responses */}
            <div data-testid="delivery-attempts">
              <div className="mb-2 promo-overline">Attempt history</div>
              {(active.attempts_history || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No attempts yet.</p>
              ) : (
                <div className="space-y-2">
                  {active.attempts_history.map((at) => (
                    <details key={at.number} className="rounded-md border border-border bg-card" data-testid={`delivery-attempt-${at.number}`}>
                      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2">
                        <span className="flex items-center gap-2">
                          {at.status === "succeeded" ? <CheckCircle size={14} weight="fill" className="text-success" /> : <XCircle size={14} weight="fill" className="text-destructive" />}
                          Attempt {at.number}
                        </span>
                        <span className="text-xs text-muted-foreground">{fmtDateTime(at.finished_at || at.started_at)}</span>
                      </summary>
                      <div className="border-t border-border px-3 py-2">
                        <div className="promo-overline mb-1">Connector response</div>
                        <pre className="max-h-48 overflow-auto rounded bg-background/60 p-2 text-[11px] leading-relaxed text-muted-foreground" data-testid={`connector-response-${at.number}`}>
{JSON.stringify(at.connector_response || at.error || {}, null, 2)}
                        </pre>
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>

            {/* Logs */}
            <div data-testid="delivery-logs">
              <div className="mb-2 promo-overline">Delivery log</div>
              <div className="space-y-1 rounded-md border border-border bg-background/40 p-3 font-mono text-[11px]">
                {(active.logs || []).map((l, i) => (
                  <div key={i} className={cn("flex gap-2", l.level === "error" ? "text-destructive" : l.level === "warn" ? "text-warning" : "text-muted-foreground")}>
                    <span className="shrink-0 opacity-60">{fmtDateTime(l.at)}</span>
                    <span>{l.message}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 sm:block">
      <span className="promo-overline">{label}</span>
      <div>{children}</div>
    </div>
  );
}
